import { useState, useEffect } from 'react';

import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

import { dummyLeads } from '@/components/mocks/leads';

interface LeadItem {
  id: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  source: string;
  date: string;
  timestamp: number;
}

const MyLeadsPage = () => {
  const [leads, setLeads] = useState<LeadItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState<LeadItem | null>(null);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  useEffect(() => {
    const savedLeads = localStorage.getItem('radhey-leads');
    if (savedLeads) {
      const parsed = JSON.parse(savedLeads);
      if (parsed.length > 0) {
        setLeads(parsed);
      } else {
        setLeads(dummyLeads);
      }
    } else {
      setLeads(dummyLeads);
    }
  }, []);

  const filteredLeads = leads
    .filter((lead) => {
      const matchesSearch =
        lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.phone.includes(searchQuery) ||
        lead.message.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) =>
      sortOrder === 'newest' ? b.timestamp - a.timestamp : a.timestamp - b.timestamp,
    );

  const handleDeleteLead = (id: string) => {
    const updated = leads.filter((l) => l.id !== id);
    setLeads(updated);
    localStorage.setItem('radhey-leads', JSON.stringify(updated));
    if (selectedLead?.id === id) setSelectedLead(null);
  };

  const handleExportCSV = () => {
    if (filteredLeads.length === 0) return;
    const headers = ['Name', 'Email', 'Phone', 'Message', 'Date'];
    const rows = filteredLeads.map((l) => [
      l.name,
      l.email,
      l.phone,
      `"${l.message.replace(/"/g, '""')}"`,
      l.date,
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `radhey-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative pt-20 pb-20 bg-gradient-to-br from-[#1A2B4A] to-[#00B8A9]">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-6xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Enquiries
          </h1>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stats Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Leads</p>
                  <p className="text-3xl font-bold text-gray-800">{leads.length}</p>
                </div>
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <i className="ri-contacts-book-line text-xl text-primary"></i>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">This Month</p>
                  <p className="text-3xl font-bold text-gray-800">
                    {leads.filter((l) => {
                      const now = new Date();
                      const leadDate = new Date(l.timestamp);
                      return leadDate.getMonth() === now.getMonth() && leadDate.getFullYear() === now.getFullYear();
                    }).length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                  <i className="ri-calendar-check-line text-xl text-teal-600"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full md:w-auto">
              <div className="relative w-full sm:w-72">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'newest' | 'oldest')}
                className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
            <button
              onClick={handleExportCSV}
              disabled={filteredLeads.length === 0}
              className="px-5 py-2.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <i className="ri-download-2-line mr-2"></i>
              Export CSV
            </button>
          </div>

          {/* Leads Table */}
          {filteredLeads.length > 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Message
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredLeads.map((lead) => (
                      <tr
                        key={lead.id}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-gray-800">{lead.name}</p>
                        </td>
                        <td className="px-6 py-4">
                          <a
                            href={`mailto:${lead.email}`}
                            className="text-sm text-primary hover:underline"
                          >
                            {lead.email}
                          </a>
                        </td>
                        <td className="px-6 py-4">
                          <a
                            href={`tel:${lead.phone}`}
                            className="text-sm text-gray-600 hover:text-primary"
                          >
                            {lead.phone}
                          </a>
                        </td>
                        <td className="px-6 py-4 max-w-xs">
                          <p className="text-sm text-gray-600 line-clamp-2">{lead.message}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{lead.date}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setSelectedLead(lead)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-primary/10 hover:text-primary transition-colors cursor-pointer"
                              title="View Details"
                            >
                              <i className="ri-eye-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteLead(lead.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-16 text-center">
              <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-inbox-line text-3xl text-gray-300"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                {leads.length === 0 ? 'No Leads Yet' : 'No Matching Leads'}
              </h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                {leads.length === 0
                  ? 'When visitors submit the contact form on your website, their information will appear here.'
                  : 'Try adjusting your search or filter criteria.'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => setSelectedLead(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedLead(null)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>

            <h3 className="text-xl font-bold text-gray-800 mb-6">Lead Details</h3>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-user-line text-primary"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Name</p>
                  <p className="text-sm font-medium text-gray-800">{selectedLead.name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-mail-line text-primary"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Email</p>
                  <a
                    href={`mailto:${selectedLead.email}`}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {selectedLead.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-phone-line text-primary"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Phone</p>
                  <a
                    href={`tel:${selectedLead.phone}`}
                    className="text-sm font-medium text-gray-800 hover:text-primary"
                  >
                    {selectedLead.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-chat-3-line text-primary"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Message</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{selectedLead.message}</p>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-400 mb-0.5">Date</p>
                <p className="text-sm text-gray-600">{selectedLead.date}</p>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <a
                href={`mailto:${selectedLead.email}`}
                className="flex-1 px-4 py-2.5 bg-primary text-white text-sm font-medium rounded-lg text-center hover:bg-primary/90 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line mr-2"></i>
                Reply via Email
              </a>
              <a
                href={`tel:${selectedLead.phone}`}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg text-center hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call
              </a>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default MyLeadsPage;
